import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import type { Edition, Product } from '../types';
import type { CommerceEntitlementFilter } from '../ui/__generated__/editionAwarenessQuery.graphql';

import { getChargeElementKey } from './get-charge-element-key';
import { getJiraApplicationKey } from './get-jira-application-key';
import { isProductOneOf } from './is-product-one-of';
import { getHamsKey } from './product-to-hams-key';

/**
 * Returns the variables required for the root Edition Awareness query.
 */
export function getQueryConfig(
	product: Product,
	edition: Edition,
	cloudId: string,
	userRolesFromProduct?: string[],
	entitlementId?: string | null,
): {
	applicationKey:
		| 'JIRA_SOFTWARE'
		| 'JIRA_PRODUCT_DISCOVERY'
		| 'JIRA_SERVICE_DESK'
		| 'JIRA_CUSTOMER_SERVICE';
	chargeElement: string;
	cloudId: string;
	// The entitlement filter ensures that we apply the `inTrialOrPreDunning` only when an entitlement is
	// on a non free edition. The filter is based on the trial end timestamp inside of TCS, this means that
	// if a customer downgrades their product to free while on a trial, then they will only show up
	// if inTrialOrPreDunning is set to true as the filter assumes they are still on trial.
	entitlementFilter: CommerceEntitlementFilter | null;
	entitlementId: string;
	isServiceCollectionFree: boolean;
	productKey: string;
	shouldCheckForAddPaymentDetails: boolean;
	shouldCheckForChargeQuantities: boolean;
	shouldCheckForDirectBuyChangeOffering: boolean;
	shouldCheckForPaymentDetails: boolean;
	shouldCheckForSinglePaidApp: boolean;
	shouldCheckForStorageLimit: boolean;
	shouldCheckForStorageUsageJira: boolean;
	shouldCheckForTrial: boolean;
	shouldUseFilteredEntitlementRelationships: boolean;
} {
	const isProductJiraFree = product === 'jira' && edition === 'free';
	const isStorageLimitsExperimentEnabled =
		edition === 'free' &&
		(product === 'jira-service-management' || product === 'jira-customer-service') &&
		FeatureGates.getExperimentValue<boolean>(
			'jsmgrowth-267_enforce_storage_limits_servco_free',
			'isEnabled',
			false,
		);

	const shouldCheckForPaymentDetails =
		edition === 'free' &&
		isProductOneOf(product, [
			'jira',
			'confluence',
			'jira-product-discovery',
			'jira-service-management',
		]);

	const isSiteAdmin = userRolesFromProduct?.includes('SITE_ADMIN') ?? false;

	// Entitlement filter controls which entitlements are fetched from the API.
	// The filter is based on the trial end timestamp in TCS - if a customer downgrades to free while
	// on a trial, they still show up when inTrialOrPreDunning is true as the filter assumes they're still on trial.
	//
	//   - Site admin + non-free edition:
	//       Returns entitlements that are (in trial/predunning) OR (in a collection)
	//
	//   - Non-site admin + non-free edition:
	//       Returns entitlements that are (in trial/predunning)
	//
	//   - Free edition (any user):
	//       Returns all entitlements (no filter)
	//
	// GATE OFF (standard behavior):
	//   - Non-free edition: Returns trial/predunning entitlements

	const getEntitlementFilter = () => {
		if (isSiteAdmin && edition !== 'free') {
			return { OR: [{ inTrialOrPreDunning: true }, { hasRelationshipFromWithType: 'COLLECTION' }] };
		} else {
			return edition !== 'free' ? { inTrialOrPreDunning: true } : null;
		}
	};
	const entitlementFilter = getEntitlementFilter();

	const resolvedEntitlementId = entitlementId ?? '';

	return {
		shouldCheckForDirectBuyChangeOffering: isProductJiraFree,
		shouldCheckForTrial: edition === 'premium' || edition === 'standard',
		cloudId,
		productKey: getHamsKey(product),
		shouldCheckForChargeQuantities: edition === 'free',
		entitlementFilter,
		entitlementId: resolvedEntitlementId,
		chargeElement: getChargeElementKey(product),
		shouldCheckForStorageUsageJira: isProductJiraFree,
		applicationKey: getJiraApplicationKey(product) ?? 'JIRA_SOFTWARE',
		shouldCheckForStorageLimit: isProductJiraFree,
		isServiceCollectionFree: isStorageLimitsExperimentEnabled,
		shouldCheckForPaymentDetails,
		shouldCheckForSinglePaidApp:
			(edition === 'standard' || edition === 'premium') &&
			Boolean(resolvedEntitlementId) &&
			fg('single_paid_app_with_auto_provision'),
		shouldCheckForAddPaymentDetails: edition === 'free',
		// When enabled, collection membership is sourced from the COLLECTION-filtered alias and
		// enterprise membership from a dedicated ENTERPRISE-filtered alias, so the unfiltered
		// `relatesFromEntitlements` fetch (which over-fetches every relationship type) is skipped.
		shouldUseFilteredEntitlementRelationships: fg('ea-filter-entitlement-relationships'),
	};
}
